import pc from 'polygon-clipping'
import type { Boundary, CircleInfo, DocumentModel, Pt, Ring, Vec2 } from './types'
import {
  circleRing,
  distToSegment,
  ensureWinding,
  pointInRing,
  ringBBox,
  roundCorners,
  signedArea,
} from './geometry'
import type { BBox } from './geometry'

// Rect corner radius clamped so opposite fillets never overlap.
function rectRadius(b: Boundary): number {
  return Math.max(0, Math.min(b.cornerRadius, Math.min(b.width, b.height) / 2 - 1e-9))
}

export function boundaryRing(doc: DocumentModel): { ring: Ring; circle: CircleInfo | null } {
  const b = doc.boundary
  const cx = doc.canvas.width / 2
  const cy = doc.canvas.height / 2
  if (b.kind === 'circle') {
    return {
      ring: circleRing(cx, cy, b.radius, doc.export.tolerance),
      circle: { cx, cy, r: b.radius },
    }
  }
  if (b.kind === 'polygon' && b.nodes.length >= 3) {
    return { ring: ensureWinding(b.nodes.map((n) => [n.x, n.y] as Pt), true), circle: null }
  }
  const hw = b.width / 2
  const hh = b.height / 2
  let ring: Ring = [
    [cx - hw, cy - hh],
    [cx + hw, cy - hh],
    [cx + hw, cy + hh],
    [cx - hw, cy + hh],
  ]
  const r = rectRadius(b)
  if (r > 0) ring = roundCorners(ring, r, doc.export.tolerance)
  return { ring, circle: null }
}

// The ring the pattern is clipped against: the boundary inset by the edge
// margin. Exact for rect (rounding included) and circle; null for polygon
// boundaries, where the margin applies through distance checks instead.
export function insetBoundaryRing(doc: DocumentModel, margin: number): Ring | null {
  if (margin <= 0) return null
  const b = doc.boundary
  const cx = doc.canvas.width / 2
  const cy = doc.canvas.height / 2
  if (b.kind === 'circle') {
    const r = b.radius - margin
    return r > 0.1 ? circleRing(cx, cy, r, doc.export.tolerance) : []
  }
  if (b.kind === 'rect') {
    const hw = b.width / 2 - margin
    const hh = b.height / 2 - margin
    if (hw <= 0.05 || hh <= 0.05) return []
    let ring: Ring = [
      [cx - hw, cy - hh],
      [cx + hw, cy - hh],
      [cx + hw, cy + hh],
      [cx - hw, cy + hh],
    ]
    const r = Math.max(0, Math.min(rectRadius(b) - margin, Math.min(hw, hh) - 1e-9))
    if (r > 0) ring = roundCorners(ring, r, doc.export.tolerance)
    return ring
  }
  return null
}

export function boundaryBBox(doc: DocumentModel): BBox {
  const b = doc.boundary
  const cx = doc.canvas.width / 2
  const cy = doc.canvas.height / 2
  if (b.kind === 'circle') {
    return { minX: cx - b.radius, minY: cy - b.radius, maxX: cx + b.radius, maxY: cy + b.radius }
  }
  if (b.kind === 'polygon' && b.nodes.length >= 3) {
    return ringBBox(b.nodes.map((n) => [n.x, n.y] as Pt))
  }
  return {
    minX: cx - b.width / 2,
    minY: cy - b.height / 2,
    maxX: cx + b.width / 2,
    maxY: cy + b.height / 2,
  }
}

export function pointInBoundary(doc: DocumentModel, p: Pt, boundary: Ring): boolean {
  const b = doc.boundary
  const cx = doc.canvas.width / 2
  const cy = doc.canvas.height / 2
  if (b.kind === 'circle') return Math.hypot(p[0] - cx, p[1] - cy) <= b.radius
  if (b.kind === 'rect') {
    return (
      Math.abs(p[0] - cx) <= b.width / 2 && Math.abs(p[1] - cy) <= b.height / 2
    )
  }
  return pointInRing(p, boundary)
}

// Signed-ish distance from a point to the boundary edge, positive inside.
export function distInsideBoundary(b: Boundary, canvasW: number, canvasH: number, x: number, y: number): number {
  const cx = canvasW / 2
  const cy = canvasH / 2
  if (b.kind === 'circle') return b.radius - Math.hypot(x - cx, y - cy)
  if (b.kind === 'polygon' && b.nodes.length >= 3) {
    const n = b.nodes.length
    let dmin = Infinity
    for (let i = 0; i < n; i++) {
      const a = b.nodes[i]
      const c = b.nodes[(i + 1) % n]
      dmin = Math.min(dmin, distToSegment({ x, y }, a, c))
    }
    const ring = b.nodes.map((p) => [p.x, p.y] as Pt)
    return pointInRing([x, y], ring) ? dmin : -dmin
  }
  // Rounded-box signed distance, positive inside (exact, including fillets).
  const r = rectRadius(b)
  const qx = Math.abs(x - cx) - (b.width / 2 - r)
  const qy = Math.abs(y - cy) - (b.height / 2 - r)
  const outside = Math.hypot(Math.max(qx, 0), Math.max(qy, 0))
  const inside = Math.min(Math.max(qx, qy), 0)
  return r - (outside + inside)
}

// Clean a user-drawn boundary polygon: resolve self-intersections via a self
// union and keep the largest outer ring. Returns null when nothing usable
// remains.
export function sanitizeBoundaryNodes(nodes: Vec2[]): Vec2[] | null {
  if (nodes.length < 3) return null
  try {
    const merged = pc.union([[nodes.map((n) => [n.x, n.y] as [number, number])]])
    let best: Ring | null = null
    let bestArea = 0
    for (const poly of merged) {
      let ring = poly[0] as Ring
      const first = ring[0]
      const last = ring[ring.length - 1]
      if (ring.length > 1 && first[0] === last[0] && first[1] === last[1]) ring = ring.slice(0, -1)
      const area = Math.abs(signedArea(ring))
      if (ring.length >= 3 && area > bestArea) {
        best = ring
        bestArea = area
      }
    }
    if (!best || bestArea < 1) return null
    return ensureWinding(best, true).map(([x, y]) => ({ x, y }))
  } catch {
    return null
  }
}
